/**
 * TimelineZones — the content-zone / content-position math for the home
 * timeline.
 *
 * Owns the zone config (recreated from HomeZonesConfig at construction), the
 * fixed-ratio content-position config, and the per-tick pipeline that writes
 * sceneProps.zones / sceneProps.zonePositions (areas -> parallax -> positions,
 * in that order). Also owns the HomeZonesConfig.showGuides debug overlay.
 *
 * IMPORTANT: the math bodies are a verbatim extraction from
 * HomeTimeline — it mutates the SAME sceneProps object the timeline
 * created (object identity is load-bearing; fields/particles hold references
 * to it). Frame values (progress, containerX, inner/virtual sizes) are
 * sampled from the timeline via narrow deps at the top of update().
 */
import { particles } from 'app/contracts/Particles';
import type { TimelineZonesDeps } from 'app/views/home/timeline/timeline.types';

import HomeZonesConfig from 'app/views/home/config/HomeZonesConfig';
import Config from 'config/settings';
import webglHome from 'config/webgl-home';

export class TimelineZones {
	private _sceneProps: particles.SharedSceneContext;
	private _deps: TimelineZonesDeps;

	//Parent for the showGuides debug graphics (the home container) — set once Pixi is ready
	private _guideContainer: PIXI.Container;

	//Ex `window.zoneGuides`: cache for the showGuides debug-overlay graphics
	private _zoneGuides: { [name: string]: { guide: PIXI.Graphics; colour: number } };

	//Frame values sampled from the timeline at the top of each update():
	private _progress: number;
	private _containerX: number;
	private _innerWidth: number;
	private _innerHeight: number;
	private _virtualWidth: number;
	private _virtualHeight: number;

	//Config as per x4 sizing
	//Created by settings in HomeZonesConfig.ts (see _createZoneConfig)
	private _contentZoneConfig: any;

	//Values live in config/webgl-home (contentPositions) — read-only here:
	private _contentPositionsConfig = webglHome.contentPositions;

	constructor(sceneProps: particles.SharedSceneContext, deps: TimelineZonesDeps) {
		this._sceneProps = sceneProps;
		this._deps = deps;

		this._createZoneConfig();
	}

	/** Set the parent container the debug zone guides draw into */
	public setGuideContainer(container: PIXI.Container): void {
		this._guideContainer = container;
	}

	/** Per-tick zone pipeline — areas, then parallax, then positions (order preserved) */
	public update(): void {
		this._progress = this._deps.getProgress();
		this._containerX = this._deps.getContainerX();

		//Size metrics come off the timeline's stable-identity sizes object (ex
		//per-metric getters). The per-update snapshot locals are kept: the read
		//timing is identical (top of update, same values throughout the pass)
		//and the hot math bodies below stay untouched:
		this._innerWidth = this._deps.sizes.innerWidth;
		this._innerHeight = this._deps.sizes.innerHeight;
		this._virtualWidth = this._deps.sizes.virtualWidth;
		this._virtualHeight = this._deps.sizes.virtualHeight;

		this._setContentZoneAreas();
		this._setContentZoneParallax();
		this._setContentZonePositions();
	}

	private _createZoneConfig(): void {
		HomeZonesConfig.createCaches();
		this._contentZoneConfig = HomeZonesConfig.computedHomeZoneConfig() as any;
	}

	private _setContentZoneAreas(): void {
		//Compress for 2.5 width timeline

		this._sceneProps.zones = {};
		this._sceneProps.zones.screens = this._setContentArea(this._contentZoneConfig.screens);
		this._sceneProps.zones.home = this._setContentArea(this._contentZoneConfig.home);
		this._sceneProps.zones.spaces = this._setContentArea(this._contentZoneConfig.spaces);
		this._sceneProps.zones.systems = this._setContentArea(this._contentZoneConfig.systems);

		if (HomeZonesConfig.showGuides) {
			this._drawZoneGuides();
		}
	}

	private _setContentZoneParallax() {
		let parallax = HomeZonesConfig.getZoneParallaxProgress(this._progress);
		this._sceneProps.zones.screens.parallax = parallax.screens;
		this._sceneProps.zones.home.parallax = parallax.home;
		this._sceneProps.zones.spaces.parallax = parallax.spaces;
		this._sceneProps.zones.systems.parallax = parallax.systems;
	}

	private _drawZoneGuides() {
		if (!this._sceneProps.zonePositions || !this._sceneProps.zonePositions.screens) {
			return;
		}

		this._drawZoneGuide(this._sceneProps.zones.screens, 'screens', 0, this._sceneProps.zonePositions.screens);
		this._drawZoneGuide(this._sceneProps.zones.home, 'home', 1, this._sceneProps.zonePositions.home);
		this._drawZoneGuide(this._sceneProps.zones.spaces, 'spaces', 2, this._sceneProps.zonePositions.spaces);
		this._drawZoneGuide(this._sceneProps.zones.systems, 'systems', 3, this._sceneProps.zonePositions.systems);
	}

	private _drawZoneGuide(zone, guideName, color, contentZoneProcessed) {
		if (!this._zoneGuides) {
			this._zoneGuides = {};
		}

		let guide;

		if (!this._zoneGuides[guideName]) {
			guide = new PIXI.Graphics();
			this._guideContainer.addChild(guide);

			let colours = [0x9b59b6, 0xf1c40f, 0xe74c3c, 0x9b59b6];

			this._zoneGuides[guideName] = {
				guide: guide,
				colour: colours[color]
			};
		}

		guide = this._zoneGuides[guideName].guide;
		guide.x = this._containerX + zone.x * this._virtualWidth;
		guide.y = zone.y * this._virtualHeight;
		guide.clear();
		guide.beginFill(this._zoneGuides[guideName].colour);

		guide.drawRect(zone.realPxPadX, zone.realPxPadY, zone.realPxWidth, zone.realPxHeight);

		guide.endFill();
		guide.alpha = 0.4;
	}

	private _setContentArea(config: any): any {
		//1. If the inner height is lower than that used in desktop, we must scale down

		const minWidthConfig =
			config.minWidthMobile && this._innerWidth <= webglHome.getSwitchScreensLayoutAt()
				? config.minWidthMobile
				: config.minWidth;
		const maxWidthConfig =
			config.maxWidthMobile && this._innerWidth <= webglHome.getSwitchScreensLayoutAt()
				? config.maxWidthMobile
				: config.maxWidth;

		let minWidth = minWidthConfig;
		let scale = 1.0;

		if (this._innerHeight < config.baseHeight) {
			scale = this._innerHeight / config.baseHeight;
			minWidth = minWidthConfig * scale;
		}

		if (this._innerWidth < minWidth) {
			scale = this._innerWidth / config.minWidth;
			minWidth = minWidthConfig * scale;
		}

		//Scale of containg elements actually needs to scale
		if (this._innerWidth < config.baseWidth) {
			let scaleFactor = 0.1;
			let r = 1 - this._innerWidth / config.baseWidth;
			scale -= scaleFactor * r;
			minWidth = minWidthConfig * scale;
		}

		//Work out final width of zone based on virtual
		//Convert that value to
		let minWidthPx = minWidthConfig * scale;
		let maxWidthPx = maxWidthConfig * scale;

		//Work out what size the zone is pixels:

		let widthRatio = this._innerWidth < 600 ? config.widthRatioMobile : config.widthRatio;
		let widthPx = widthRatio ? widthRatio * this._innerWidth * scale : this._innerWidth * scale;

		if (widthPx < minWidthPx) widthPx = minWidthPx;
		else if (widthPx > maxWidthPx) widthPx = maxWidthPx;

		if (widthRatio && widthPx > widthRatio * this._innerWidth * scale) {
			widthPx = widthRatio * this._innerWidth;
		}

		if (
			this._innerWidth <= HomeZonesConfig.noOverlapSize &&
			config.noOvelapWidthRatio &&
			widthPx > config.noOvelapWidthRatio * this._innerWidth
		) {
			widthPx = config.noOvelapWidthRatio * this._innerWidth;
			scale = widthPx / config.minWidth;
		}

		let scaleBump = 1;
		let mobileHeightLimitPx = 0;

		if (this._innerWidth < webglHome.getSwitchScreensLayoutAt()) {
			//Scale up whole area by amount so size change is not so extreme:
			if (config.mobileScaleBump) scaleBump = config.mobileScaleBump;

			//Stop mobile layout getting too tall
			if (config.mobileMaxHeightRatio) mobileHeightLimitPx = this._innerHeight * config.mobileMaxHeightRatio;
		}

		scale *= scaleBump;

		let heightPx = config.baseHeight * scale;

		//Don't size mobile
		if (mobileHeightLimitPx && heightPx > mobileHeightLimitPx) {
			const diff = mobileHeightLimitPx / heightPx;
			heightPx = mobileHeightLimitPx;
			scale *= diff;
			widthPx *= diff;
		}

		//Relative w/h in scene cords:
		let w = widthPx / this._virtualWidth;
		let h = heightPx / this._innerHeight;

		//The difference between min/max ratio is how cropped the layout is.
		//THis can be used to scale / move items to deal with spacing issues as the layout become thinner
		let cropRatio = (widthPx - minWidthPx) / (maxWidthPx - minWidthPx);

		if (cropRatio > 1) cropRatio = 1;

		//Vertical height is important to position UI in correct space
		//So use designed size:
		let sizeYRatio = this._innerHeight / 1009;
		let sizeYDiffPx = 1009 - this._innerHeight;

		if (sizeYRatio > 1) sizeYRatio = 1;

		if (sizeYDiffPx < 0) sizeYDiffPx = 0;
		else if (sizeYDiffPx > 100) sizeYDiffPx = 100;

		//This is the actual effective area after resizing and allow croping:

		const cropRangePx = maxWidthPx - minWidthPx;
		const realXCropPx = cropRatio * cropRangePx;
		const realWidthPx = widthPx;
		const realHeightPx = heightPx * sizeYRatio;

		let xshift = 0;
		const shiftFrom = config.xShiftFrom ? config.xShiftFrom : 1500;
		const mobileStopShift = Config.getCurrentMobileSwitch();

		if (config.xShift && this._innerWidth < shiftFrom && this._innerWidth > mobileStopShift) {
			const shiftTo = config.xShiftTo ? config.xShiftTo : HomeZonesConfig.noOverlapSize;
			const xShiftRange = shiftFrom - shiftTo;
			xshift = Math.min(config.xShift, config.xShift - config.xShift * ((this._innerWidth - shiftTo) / xShiftRange));
		}

		let configCy =
			config.cyNoOverlap && this._innerWidth <= webglHome.getSwitchScreensLayoutAt() ? config.cyNoOverlap : config.cy;

		return {
			scale: scale,
			x: config.cx - w / 2 + xshift,
			y: configCy - h / 2,
			h: h,
			w: w,
			cx: config.cx,
			cy: configCy,
			widthPx: widthPx,
			minWidthPx: minWidthPx,
			maxWidthPx: maxWidthPx,
			heightPx: heightPx,
			cropRatio: cropRatio,
			sizeYRatio: sizeYRatio,
			sizeYDiffPx: sizeYDiffPx,
			sizeDiffRel: sizeYDiffPx / this._innerHeight,
			realPxWidth: realWidthPx,
			realPxHeight: realHeightPx,
			cropRangePx: cropRangePx,
			realXCropPx: realXCropPx,

			//Save x1/x2/cx
			realPxPadX: (widthPx - realWidthPx) / 2,
			realPxPadY: (heightPx - realHeightPx) / 2,
			scaleBump: scaleBump
		};
	}

	private _zoneCheckBox = false;
	private _setContentZonePositions(): void {
		this._sceneProps.zonePositions = {};

		this._zoneCheckBox = true;
		this._sceneProps.zonePositions.box = this._setContentPosition(this._contentPositionsConfig.box);
		this._zoneCheckBox = false;

		this._sceneProps.zonePositions.systemsPArea = this._setContentPosition(this._contentPositionsConfig.systemsPArea);

		this._sceneProps.zonePositions.homeParticles = this._setContentPosition(this._contentPositionsConfig.homeParticles);
		this._sceneProps.zonePositions.home = this._sceneProps.zonePositions.homeParticles;

		this._sceneProps.zonePositions.spacesParticles = this._setContentPosition(
			this._contentPositionsConfig.spacesParticles
		);
		this._sceneProps.zonePositions.spaces = this._sceneProps.zonePositions.spacesParticles;

		this._sceneProps.zonePositions.screensParticles = this._setContentPosition(
			this._contentPositionsConfig.screensParticles
		);
		this._sceneProps.zonePositions.screens = this._sceneProps.zonePositions.screensParticles;

		this._sceneProps.zonePositions.systemsParticles = this._setContentPosition(
			this._contentPositionsConfig.systemsParticles
		);
		this._sceneProps.zonePositions.systems = this._sceneProps.zonePositions.systemsParticles;
	}

	/**
	 * Set x/y/w/h scene positions for a fixed ratio element set in pixels (Size as designed at 100%)
	 * This can used to set particle positions/boundaries/masks/ (Anything 'virtual')
	 * @param config
	 */
	private _setContentPosition(config: any): any {
		let zone = this._sceneProps.zones[config.zone];

		let scale = zone.scale;
		let zoneY = zone.y;

		if (this._zoneCheckBox && this._innerWidth <= webglHome.getSwitchScreensLayoutAt()) {
			scale *= 0.9;
			zoneY += 0.017;
		}

		let heightPx = config.heightPx * scale;
		let widthPx = config.widthPx * scale;
		let h = heightPx / this._innerHeight;
		let w = widthPx / this._virtualWidth;

		let cx = zone.x + config.cx * zone.w;

		let x = cx - w / 2;

		let configCy = config.cy;

		let cy = zoneY + configCy * zone.h;

		let y = cy - h / 2;
		let relWidth = widthPx / zone.widthPx;
		let relHeight = heightPx / zone.heightPx;
		let relX = config.cx - relWidth / 2;
		let relY = configCy - relHeight / 2;

		let moveY = zone.sizeDiffRel * -0.5;
		moveY = 0;

		return {
			//Set height/width when at 100% scale (Designed scale):
			baseWidth: config.widthPx,
			baseHeight: config.heightPx,

			//Actual pre scale width:
			scale: scale,
			widthPx: widthPx,
			heightPx: heightPx,

			//Translated scene positions:
			x: x,
			y: y + moveY,
			w: w,
			h: h,
			cx: cx,
			cy: cy + moveY,

			//Relative to zone:
			relX: relX,
			relY: relY,
			relCx: config.cx,
			relCy: configCy,
			relW: relWidth,
			relH: relHeight
		};
	}
}
